import { collection, doc, getDoc, getDocs, query, setDoc, updateDoc, where } from "firebase/firestore"
import { auth, db } from "../../config/firebase"

export enum ROUND_STATUS {
  Delete = "Delete",
  Revealed = "Revealed",
  Scrambling = "Scrambling",
  Scrambled = "Scrambled",
  ScrambleVerified = "Scramble Verified",
  ReadyToSolve = "Ready to Solve",
  Solving = "Solving",
  Solved = "Solved",
  SolveVerified = "Solve Verified"
}

export interface RoundSolve {
  roundNumber: number,
  solveNumber: number,
  solveTime: number | null,
  scramble: string,
  status: ROUND_STATUS
}

export interface Match {
  id: string,
  createdAt: Date,
  currentRound: number
  currentSolve: number
  status: "scheduled" | "in_progress" | "completed"
  winner: -1 | 1 | 2,
  uids: string[],
  players?: Player[],
  rounds?: Round[],
  streamids: string[],
}

export interface Player {
  createdAt: Date,
  uid: string,
  displayName: string,
  email: string
}

export interface Round {
  scrambles: string[]
}

export async function updateCurrentRound(
  matchId: string,
  currentRound: number
): Promise<void> {
  const matchRef = doc(db, "matches", matchId);

  await updateDoc(matchRef, {
    currentRound,
  });
}

export async function updateCurrentRoundSolve(
  matchId: string,
  currentRound: number,
  currentSolve: number
): Promise<void> {
  const matchRef = doc(db, "matches", matchId);

  await updateDoc(matchRef, {
    currentRound,
    currentSolve,
  });
}

/**
 * Updates a player's round solve details in Firestore.
 *
 * @param db - Firestore instance
 * @param matchId - The ID of the match document
 * @param playerId - The ID of the player
 * @param roundNumber - The round number
 * @param solveNumber - The solve number within the round
 * @param scramble - The scramble for the solve
 * @param solveTime - The time it took to solve
 */
export async function updatePlayerRoundSolve(
  matchId: string,
  playerId: string,
  roundNumber: number,
  solveNumber: number,
  scramble: string,
  solveTime: number | null,
  status: ROUND_STATUS
): Promise<void> {
  try {
    const solveId = `round${roundNumber}-solve${solveNumber}`;
    const solveRef = doc(
      db,
      "matches",
      matchId,
      "players",
      playerId,
      "round-solves",
      solveId
    );

    await setDoc(solveRef, {
      roundNumber,
      solveNumber,
      scramble,
      solveTime,
      status
    });
  } catch (error) {
    console.error("Error updating player round solve:", error);
    throw error;
  }
}

/**
 * Updates a player's round solve details in Firestore.
 *
 * @param db - Firestore instance
 * @param matchId - The ID of the match document
 * @param playerId - The ID of the player
 * @param roundNumber - The round number
 * @param solveNumber - The solve number within the round
 * @param scramble - The scramble for the solve
 * @param solveTime - The time it took to solve
 */
export async function updatePlayerRoundSolveStatus(
  matchId: string,
  playerId: string,
  roundNumber: number,
  solveNumber: number,
  solveTime: number | null,
  status: ROUND_STATUS
): Promise<void> {
  try {
    const solveId = `round${roundNumber}-solve${solveNumber}`;
    const solveRef = doc(
      db,
      "matches",
      matchId,
      "players",
      playerId,
      "round-solves",
      solveId
    );

    await updateDoc(solveRef, {
      roundNumber,
      solveNumber,
      solveTime,
      status
    });
  } catch (error) {
    console.error("Error updating player round solve:", error);
    throw error;
  }
}


export async function getMatchesForPlayer(): Promise<Record<string, Match>> {
  const matchesRef = collection(db, "matches");

  // Query matches where uids array contains the authUid
  const matchesQuery = query(matchesRef, where("uids", "array-contains", auth.currentUser?.uid));
  const matchesSnap = await getDocs(matchesQuery);

  const matches: Record<string, Match> = {};

  matchesSnap.docs.forEach((doc) => {
    matches[doc.id] = doc.data() as Match
  });

  return matches;
}

export async function getMatch(matchId: string | undefined): Promise<Match | null> {

  if (matchId === undefined) return null;

  const matchRef = doc(db, "matches", matchId);
  const matchSnap = await getDoc(matchRef);

  if (!matchSnap.exists()) {
    throw new Error(`Match with ID ${matchId} not found.`);
  }

  const matchData = matchSnap.data() as Match;

  // Fetch the players subcollection
  const playersRef = collection(matchRef, "players");
  const playersSnap = await getDocs(playersRef);

  const players: Player[] = [];
  for (const playerDoc of playersSnap.docs) {
    const playerData = playerDoc.data() as Player;
    players.push(playerData)
  }

  const roundsRef = collection(matchRef, "rounds");
  const roundsSnap = await getDocs(roundsRef);
  const rounds: Round[] = [];
  for (const roundDoc of roundsSnap.docs) {
    const roundData = roundDoc.data() as Round;
    rounds.push(roundData)
  }

  return {
    ...matchData,
    players: players,
    rounds: rounds,
  };
}

export interface SolveDifference {
  roundNumber: number;
  solveNumber: number;
  timeDifference: number;
}

export const compareRoundSolves = (
  array1: RoundSolve[],
  array2: RoundSolve[]
): SolveDifference[] => {
  const differences: SolveDifference[] = [];
  if(array1 === undefined || array2 === undefined) return differences;

  // Iterate through array1 and find matching solves in array2
  for (const solve1 of array1) {
    const solve2 = array2.find(
      (solve) =>
        solve.roundNumber === solve1.roundNumber &&
        solve.solveNumber === solve1.solveNumber
    );

    // Both solves must have a solveTime to calculate the difference
    if (solve1.solveTime !== null && solve2 !== undefined && solve2.solveTime !== null) {
      differences.push({
        roundNumber: solve1.roundNumber,
        solveNumber: solve1.solveNumber,
        timeDifference: solve1.solveTime - solve2.solveTime
      });
    }
  }

  return differences;
};

export const countWinsForRound = (
  differences: SolveDifference[],
  roundNumber: number
): number => {
  return differences.filter(
    (diff) => diff.roundNumber === roundNumber && diff.timeDifference < 0
  ).length;
};

export const countLossesForRound = (
  differences: SolveDifference[],
  roundNumber: number
): number => {
  return differences.filter(
    (diff) => diff.roundNumber === roundNumber && diff.timeDifference > 0
  ).length;
};

export const getFastestTimeForRound = (
  solves: RoundSolve[],
  roundNumber: number
): number | null => {
  const validTimes = solves
    .filter(
      (solve) =>
        solve.roundNumber === roundNumber && solve.solveTime !== null
    ) // Filter by roundNumber and valid solveTime
    .map((solve) => solve.solveTime as number); // Extract solveTime values

  if (validTimes.length === 0) return null; // No valid times found

  return Math.min(...validTimes); // Find and return the smallest solveTime
};

export const getAverageTimeForRound = (
  solves: RoundSolve[],
  roundNumber: number
): number | null => {
  const validTimes = solves
    .filter(
      (solve) =>
        solve.roundNumber === roundNumber && solve.solveTime !== null
    ) // Filter by roundNumber and valid solveTime
    .map((solve) => solve.solveTime as number); // Extract solveTime values

  if (validTimes.length === 0) return null; // No valid times found

  const sum = validTimes.reduce((total, time) => total + time, 0); // Sum all valid times
  return sum / validTimes.length; // Calculate and return the average
};

export const countRoundsWon = (
  playerSolves: RoundSolve[],
  opponentSolves: RoundSolve[],
  setMax: number
): number => {
  // Group solves by roundNumber for each player
  const groupByRound = (solves: RoundSolve[]): Record<number, RoundSolve[]> =>
    solves.reduce((acc, solve) => {
      if (!acc[solve.roundNumber]) acc[solve.roundNumber] = [];
      acc[solve.roundNumber].push(solve);
      return acc;
    }, {} as Record<number, RoundSolve[]>);

  const playerGrouped = groupByRound(playerSolves);
  const opponentGrouped = groupByRound(opponentSolves);

  let roundsWon = 0;

  // Compare each round
  for (const roundNumber in playerGrouped) {
    const playerRoundSolves = playerGrouped[roundNumber];
    const opponentRoundSolves = opponentGrouped[Number(roundNumber)] || [];

    let playerWins = 0;

    // Compare solves within the round
    for (const playerSolve of playerRoundSolves) {
      const opponentSolve = opponentRoundSolves.find(
        (solve) => solve.solveNumber === playerSolve.solveNumber
      );

      if (
        opponentSolve &&
        playerSolve.solveTime !== null &&
        opponentSolve.solveTime !== null &&
        playerSolve.solveTime < opponentSolve.solveTime
      ) {
        playerWins++;
      }
    }

    // A player wins the round if they win at least `setMax` solves
    if (playerWins >= setMax) roundsWon++;
  }

  return roundsWon;
};